import { TFunction } from 'i18next';
import {
    ModelPlotBy,
    ModelScaleBy,
    ProportionsOTThresholdType,
    ProportionsOTBenchmarkType,
    ProportionsOTCountryBenchmarkType,
    ModelPlotCalculation,
    ControlHubModelPeriodType,
    AutomationRange,
} from 'types/graphql-global-types';

export type RowDataSource = {
    id: string;
    name: string;
    table: string;
};

export type YAxisData = {
    id: string;
    name: string;
};

export type ScaleBy = {
    id: ModelScaleBy;
    name: string;
};

export type PlotBy = {
    id: ModelPlotBy;
    name: string;
};

export type CumulativeOrTimePeriod = {
    id: ModelPlotCalculation;
    name: string;
};

export type ThresholdType = {
    id: ProportionsOTThresholdType;
    name: string;
};
export type EligibilityPeriod = {
    id: ControlHubModelPeriodType;
    name: string;
};
export type BenchmarkType = {
    id: ProportionsOTBenchmarkType;
    name: string;
};
export type CountryType = {
    id: ProportionsOTCountryBenchmarkType;
    name: string;
};

export type RowDataDate = {
    id: string;
    name: string;
};

export type RowDataFlag = {
    id: string;
    name: string;
};

export type Range = {
    id: AutomationRange;
    name: string;
};

export type IFormData = {
    // Column 1
    isActive: boolean;
    configId: string;
    plotTitle: string;
    xAxisTitle: string;
    yAxisTitle: string;
    qtl?: { id: string; name: string } | null;
    reportOOT?: { id: string; name: string } | null;

    // Column 2

    rowData: RowDataSource | null;
    rowDataDate: RowDataDate | null;
    rowDataFlag: RowDataFlag | null;
    scaleBy?: ScaleBy | null;
    plotBy?: PlotBy | null;
    plotCalculations?: CumulativeOrTimePeriod | null;
    automationRange?: Range | null;
    numberOfTimePeriods: number | null;
    xAxisPtsToShade: number | null;
    expectedVolume: number | null;
    minData: number | null;
    // Column 3

    thresholdType: ThresholdType | null;
    upperRed: number | null;
    upperAmber: number | null;
    lowerRed: number | null;
    lowerAmber: number | null;
    eligibilityUnit: number | null;
    eligibilityPeriod: EligibilityPeriod | null;

    // Column 4
    studyType: BenchmarkType | null;
    countryValue: CountryType | null;
    studySpecificValue: number | null;
    numeratorName: string | null;
    denominatorName: string | null;
};

export const OptionsProportionsOT = (t: TFunction) => {
    return {
        ScaleByOptions: [
            { id: ModelScaleBy._1, name: t(ModelScaleBy._1) },
            { id: ModelScaleBy._10, name: t(ModelScaleBy._10) },
            { id: ModelScaleBy._100, name: t(ModelScaleBy._100) },
            { id: ModelScaleBy._1000, name: t(ModelScaleBy._1000) },
        ],
        EligibilityPeriodOptions: [
            { id: ControlHubModelPeriodType.DAYS, name: t(ControlHubModelPeriodType.DAYS) },
            { id: ControlHubModelPeriodType.WEEKS, name: t(ControlHubModelPeriodType.WEEKS) },
            { id: ControlHubModelPeriodType.MONTHS, name: t(ControlHubModelPeriodType.MONTHS) },
            { id: ControlHubModelPeriodType.QUARTERS, name: t(ControlHubModelPeriodType.QUARTERS) },
            { id: ControlHubModelPeriodType.YEARS, name: t(ControlHubModelPeriodType.YEARS) },
        ],
        PlotByOptions: [
            { id: ModelPlotBy.MONTHS, name: t(ModelPlotBy.MONTHS) },
            { id: ModelPlotBy.WEEKS, name: t(ModelPlotBy.WEEKS) },
        ],
        ThresholdTypeOptions: [
            { id: ProportionsOTThresholdType.FIXED, name: t(ProportionsOTThresholdType.FIXED) },
            { id: ProportionsOTThresholdType.LANEY_P_CHART, name: t(ProportionsOTThresholdType.LANEY_P_CHART) },
            { id: ProportionsOTThresholdType.P_CHART, name: t(ProportionsOTThresholdType.P_CHART) },
            {
                id: ProportionsOTThresholdType.QUASI_BINOMIAL_P_CHART,
                name: t(ProportionsOTThresholdType.QUASI_BINOMIAL_P_CHART),
            },
        ],
        BenchmarkTypeOptions: [
            { id: ProportionsOTBenchmarkType.NONE, name: t(ProportionsOTBenchmarkType.NONE) },
            { id: ProportionsOTBenchmarkType.STUDY_SPECIFIC, name: t(ProportionsOTBenchmarkType.STUDY_SPECIFIC) },
            { id: ProportionsOTBenchmarkType.STUDY_RATE, name: t(ProportionsOTBenchmarkType.STUDY_RATE) },
        ],
        CountryBenchmarkTypeOptions: [
            {
                id: ProportionsOTCountryBenchmarkType.COUNTRY_RATE,
                name: t(ProportionsOTCountryBenchmarkType.COUNTRY_RATE),
            },
            { id: ProportionsOTCountryBenchmarkType.NONE, name: t(ProportionsOTCountryBenchmarkType.NONE) },
            {
                id: ProportionsOTCountryBenchmarkType.STUDY_SPECIFIC,
                name: t(ProportionsOTCountryBenchmarkType.STUDY_SPECIFIC),
            },
            { id: ProportionsOTCountryBenchmarkType.STUDY_RATE, name: t(ProportionsOTCountryBenchmarkType.STUDY_RATE) },
            //  Commenting them as the config user story doesn't mention this type.
            //  {
            //     id: ProportionsOTCountryBenchmarkType.STUDY_SITE_MEAN,
            //     name: t(ProportionsOTCountryBenchmarkType.STUDY_SITE_MEAN),
            //  },

            //  Commenting them as the config user story doesn't mention this type.
            //  {
            //      id: ProportionsOTCountryBenchmarkType.COUNTRY_SITE_MEAN,
            //      name: t(ProportionsOTCountryBenchmarkType.COUNTRY_SITE_MEAN),
            //  },
        ],
        PlotCalculationOptions: [
            { id: ModelPlotCalculation.CUMULATIVE, name: t(ModelPlotCalculation.CUMULATIVE) },
            { id: ModelPlotCalculation.BY_TIME_PERIOD, name: t(ModelPlotCalculation.BY_TIME_PERIOD) },
        ],
        YesNoOptions: [
            { id: 'true', name: 'Yes' },
            { id: 'false', name: 'No' },
        ],
        RangeOptions: [
            { id: AutomationRange.ALL, name: t(AutomationRange.ALL) },
            { id: AutomationRange.SPECIFIC_RANGE, name: t(AutomationRange.SPECIFIC_RANGE) },
        ],
    };
};
